/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { Diagram, Bounds, DiagramLabelPosition, States } from './Diagram.types';
import type { Size } from '../utils/UNSAFE_size';
import type { TextDimensions } from '../hooks/PRIVATE_useTextDimensions';

import { DiagramLayers } from './DiagramLayers';
import { TrackResizeContainer } from '../PRIVATE_TrackResizeContainer';
import { DiagramPanZoomContainer } from './DiagramPanZoomContainer';

import { useEvents } from './hooks/useEvents';
import { useDiagramDatatip } from './hooks/useDiagramDatatip';
import { useTextDimensions } from '../hooks/PRIVATE_useTextDimensions';
import { usePanZoom } from './hooks/usePanZoom';
import { useRef, useState } from 'preact/hooks';
import { useUser } from '../hooks/UNSAFE_useUser';
import { useDiagramSelection } from './hooks/useDiagramSelection';

import { mergeProps } from '../utils/UNSAFE_mergeProps';
import {
  constructLayoutJSON,
  deconstructLayoutJSON,
  createItemMap,
  getDatatipText,
  getFocusedItemBounds,
  mergeDimensions,
  getDimensionsFromBounds,
  combineIds,
  getContentBounds,
  centerXYToPanXY,
  filterLinksWithNoEndNodes,
  getAllLabelBounds
} from './utils/diagramUtils';
import { sizeToCSS } from '../utils/UNSAFE_size';

import { outerStyles } from './themes/DiagramStyles.css';

/**
 * Diagrams are used to display a set of nodes and the links between them.
 */
export function Diagram<K1 extends string | number, K2 extends string | number>({
  nodes = [],
  links = [],
  width = '100%',
  height = '140x',
  selectedIds = {},
  selectionMode = 'none',
  layout,
  nodeRenderer,
  linkRenderer,
  onSelectionChange,
  onItemInput,
  highlightedIds = {},
  datatip,
  ...otherProps
}: Diagram<K1, K2>) {
  const focusedItemBoundsRef = useRef<Bounds | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  //TODO JET-62563 change bounds to a MAP
  const [bounds, setBounds] = useState<Bounds[]>([]);
  const [combinedLabelBounds, setCombinedLabelBounds] = useState<Bounds>();
  const { direction } = useUser();

  if (nodes.length === 0) return null;
  const diagramNodes = nodes;
  const nodeMap = createItemMap<K1, K2>(diagramNodes);
  const nodeIdToDataMap = nodeMap.itemIdToDataMap;
  const diagramLinks = filterLinksWithNoEndNodes(links, nodeIdToDataMap);
  const linkMap = createItemMap<K1, K2>(diagramLinks);
  const linkIdToDataMap = linkMap.itemIdToDataMap;

  return (
    <TrackResizeContainer
      width={sizeToCSS(width as Size) || '0px'}
      height={sizeToCSS(height as Size) || '0px'}>
      {function _(dgWidth, dgHeight) {
        const resizeThrottling = useRef<boolean>(false);
        const newDimensions = getDimensionsFromBounds(bounds);
        const { textMeasureContent, getTextDimensions } = useTextDimensions();
        const labelDimensions = useRef<Map<K1 | K2, TextDimensions>>(new Map());
        const nodeLabelPosRef = useRef<(DiagramLabelPosition | undefined)[] | undefined>([]);
        const linkLabelPosRef = useRef<(DiagramLabelPosition | undefined)[] | undefined>([]);

        const contentBounds = getContentBounds(bounds, combinedLabelBounds);
        const { panZoomProps, cursor, centerX, centerY, zoomValue, kbPanCallback, kbZoomCallback } =
          usePanZoom({
            props: otherProps,
            captureTargetRef: rootRef,
            width: dgWidth,
            height: dgHeight,
            contentBounds
          });
        const { focusedItemInfo, hoveredItemInfo, ...eventsProps } = useEvents<K1, K2>(
          diagramNodes[0],
          diagramNodes,
          diagramLinks,
          bounds,
          linkIdToDataMap,
          nodeMap.itemIdToIndexMap,
          dgWidth,
          dgHeight,
          otherProps.maxZoom || 1,
          onItemInput,
          kbPanCallback,
          kbZoomCallback
        );

        const selectionProps = useDiagramSelection<K1, K2>({
          selectedIds,
          selectionMode,
          nodeMap,
          linkMap,
          focusedItemInfo,
          onSelectionChange,
          rootRef
        });

        // This is for the datatip kb position
        if (focusedItemInfo && bounds.length > 0) {
          focusedItemBoundsRef.current = getFocusedItemBounds(
            focusedItemInfo,
            bounds,
            diagramLinks,
            nodeMap.itemIdToIndexMap,
            linkMap.itemIdToIndexMap
          );
        }
        const datatipText = getDatatipText(
          nodeIdToDataMap,
          linkIdToDataMap,
          focusedItemInfo,
          hoveredItemInfo,
          datatip
        );
        const { datatipContent, datatipProps } = useDiagramDatatip({
          text: datatipText,
          focusedItemInfo,
          focusedItemBoundsRef,
          panState: centerXYToPanXY(centerX, centerY, zoomValue, dgWidth, dgHeight),
          zoom: zoomValue,
          width: dgWidth
        });

        const style = {
          width: dgWidth,
          height: dgHeight,
          cursor: cursor
        };

        const states = useRef<States<K1, K2>>();
        const previousState = states.current // states.current is previous state
          ? states.current
          : {
              selectedNodeIds: [],
              selectedLinkIds: [],
              focusedId: undefined,
              hoveredId: undefined,
              activeId: undefined,
              highlightedIds: [],
              zoom: 0
            };

        states.current = {
          // update states to new state
          selectedNodeIds: selectedIds.nodes ?? [],
          selectedLinkIds: selectedIds.links ?? [],
          focusedId:
            focusedItemInfo.isCurrent && focusedItemInfo.isFocusVisible
              ? focusedItemInfo.id
              : undefined,
          hoveredId: hoveredItemInfo?.isCurrent ? hoveredItemInfo.id : undefined,
          activeId: eventsProps['aria-activedescendant'],
          highlightedIds: combineIds(highlightedIds.nodes, highlightedIds.links),
          zoom: zoomValue
        };

        const mergedEventProps = mergeProps(
          panZoomProps,
          eventsProps,
          selectionProps,
          datatipProps
        );

        // Called anytime node changes size
        const onNodeSizeChanged = (nodeWidth: number, nodeHeight: number, idx: number) => {
          newDimensions[idx] = { w: nodeWidth, h: nodeHeight };
          if (resizeThrottling.current) {
            return;
          }

          resizeThrottling.current = true;
          // performs layout at the next available animation frame when all measurements should be ready
          window.requestAnimationFrame(() => {
            const { labelDims, layoutJSON } = constructLayoutJSON(
              diagramNodes,
              nodeIdToDataMap,
              linkIdToDataMap,
              newDimensions,
              dgWidth,
              dgHeight,
              getTextDimensions
            );
            labelDimensions.current = labelDims;
            const { nodePoints, nodeLabelPos, linkLabelPos } = deconstructLayoutJSON<K1, K2>(
              layout(layoutJSON),
              diagramNodes,
              diagramLinks
            );
            nodeLabelPosRef.current = nodeLabelPos;
            linkLabelPosRef.current = linkLabelPos;
            const nodeBounds = mergeDimensions(newDimensions, nodePoints);
            setBounds(nodeBounds);
            const labelBounds = getAllLabelBounds(
              labelDims,
              diagramNodes,
              diagramLinks,
              nodeLabelPos,
              linkLabelPos,
              direction === 'rtl'
            );
            setCombinedLabelBounds(labelBounds);
            resizeThrottling.current = false;
          });
        };
        return (
          <div
            ref={rootRef}
            class={outerStyles}
            {...mergedEventProps}
            style={style}
            tabIndex={0}
            role="application"
            aria-label={otherProps['aria-label']}
            aria-labelledby={otherProps['aria-labelledby']}
            aria-describedby={otherProps['aria-describedby']}>
            {getTextDimensions ? (
              <DiagramPanZoomContainer
                centerX={centerX}
                centerY={centerY}
                zoom={zoomValue}
                width={dgWidth}
                height={dgHeight}>
                <DiagramLayers
                  nodes={diagramNodes}
                  links={diagramLinks}
                  nodeRenderer={nodeRenderer}
                  linkRenderer={linkRenderer}
                  width={dgWidth}
                  height={dgHeight}
                  nodeBounds={bounds}
                  states={states.current!}
                  previousStates={previousState}
                  onNodeSizeChanged={onNodeSizeChanged}
                  nodeLabelPositions={nodeLabelPosRef.current}
                  linkLabelPositions={linkLabelPosRef.current}
                  labelDimensions={labelDimensions.current}
                  supportsSelection={
                    selectionMode !== 'none' ||
                    !!(selectedIds?.nodes && selectedIds.nodes.length > 0) ||
                    !!(selectedIds?.links && selectedIds.links.length > 0)
                  }
                />
              </DiagramPanZoomContainer>
            ) : undefined}
            {textMeasureContent}
            {datatipContent}
          </div>
        );
      }}
    </TrackResizeContainer>
  );
}
